import { createElement, useCallback, useMemo, useState } from 'react';
import { PHIEU_XUAT_KHO_TABLE_TABS, TABLE_PAGE_SIZE } from '../constants';
import { initialSearchValues, SearchFormValues } from '../schema';
import { usePhieuXuatKhoSearchContext } from './search-context';
import { useMultipleForms, useSearchTable } from '../hooks';
import { getDataTableColumns } from '../cols-definition';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

export const usePhieuXuatKhoTable = () => {
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchFormValues>(initialSearchValues);
  const { hasInvoiceParams } = usePhieuXuatKhoSearchContext();

  const {
    data,
    detail,
    fetchDetail,
    addItem,
    currentPage,
    deleteItem,
    refreshData,
    isLoading,
    handlePageChange,
    totalItems,
    updateItem,
    sumTotal,
    searchData,
    isFiltered
  } = useCRUD({
    endpoint: QUERY_KEYS.PHIEU_XUAT_KHO,
    initialFetch: !hasInvoiceParams,
    pageSize: TABLE_PAGE_SIZE,
    sumField: 't_tien_nt',
    useFilter: true
  });

  const {
    showForm,
    showDelete,
    showBulkDelete,
    handleCloseBulkDelete,
    formMode,
    isCopyMode,
    selectedObj,
    selectedRowIndex,
    handleRowClick,
    clearSelection,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick
  } = useForms(fetchDetail);

  const multipleForms = useMultipleForms({
    addPhieuXuatKho: addItem,
    editPhieuXuatKho: updateItem
  });

  const {
    searchState,
    handleSearchChange,
    onPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering
  } = useSearchTable({
    searchData,
    searchParams,
    setSearchParams,
    isFiltered,
    handlePageChange
  });

  const handleSearchOpen = useCallback(() => {
    setShowSearchDialog(true);
  }, []);

  const handleSearchClose = useCallback(() => {
    setShowSearchDialog(false);
  }, []);

  const handleSearchSubmit = useCallback(
    async (filters: SearchFormValues) => {
      await handleInitialSearch(filters);
      setShowSearchDialog(false);
    },
    [handleInitialSearch]
  );

  const handleRefresh = useCallback(async () => {
    await handleRefreshClick();
    await fetchDetail(selectedObj?.uuid);
  }, [fetchDetail, handleRefreshClick, selectedObj?.uuid]);

  const displayData = hasFilterParams ? searchState.searchResults : data;
  const displayIsLoading = isLoading || isFiltering;
  const totalAmount = hasFilterParams ? (searchState.totalRow?.t_tien_nt ?? sumTotal) : sumTotal;
  const columns = useMemo(
    () => getDataTableColumns(handleViewClick, totalAmount, displayIsLoading),
    [displayIsLoading, handleViewClick, totalAmount]
  );

  const tables = useMemo(
    () =>
      PHIEU_XUAT_KHO_TABLE_TABS.map(tab => ({
        name: tab.name,
        rows: tab.status ? displayData.filter(row => String(row?.status ?? '') === tab.status) : displayData,
        columns,
        icon: tab.color
          ? createElement('div', {
              className: 'mr-2 size-[7px] rounded-full',
              style: { backgroundColor: tab.color }
            })
          : undefined
      })),
    [columns, displayData]
  );

  return {
    crud: {
      detail,
      addItem,
      deleteItem,
      updateItem,
      refreshData
    },
    selection: {
      selectedObj,
      selectedRowIndex,
      clearSelection
    },
    dialogs: {
      showForm,
      showDelete,
      showBulkDelete,
      isCopyMode
    },
    form: {
      formMode,
      handleCloseForm,
      handleCloseDelete,
      handleAddClick,
      handleEditClick,
      handleDeleteClick,
      handleCopyClick,
      handleRowClick,
      handleCloseBulkDelete
    },
    searchDialog: {
      open: showSearchDialog,
      searchParams,
      onOpen: handleSearchOpen,
      onClose: handleSearchClose,
      onSubmit: handleSearchSubmit
    },
    multipleFormDialog: {
      open: multipleForms.showMultipleForms,
      formMode: multipleForms.formMode,
      invoiceData: multipleForms.invoiceData,
      initialData: multipleForms.initialData,
      onSubmit: multipleForms.handleMultipleFormSubmit,
      onClose: multipleForms.handleCloseMultipleForms
    },
    table: {
      tables,
      columns,
      displayData,
      displayIsLoading,
      currentPage: hasFilterParams ? searchState.currentPage : currentPage,
      totalItems: hasFilterParams ? searchState.totalCount : totalItems,
      onPageChange,
      onSearchChange: handleSearchChange,
      onRefresh: handleRefresh
    }
  };
};
